import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { useTripAssets, updateAssetGradeRef } from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { captureFrameAt } from './ClipThumb';
import { scaleToDataUrl } from '../utils/stageAssist';
import { fmtTime } from '../utils/movie';
import { httpsify } from '../utils/url';

// Reference frame match — pair keyframes of the RAW clip being graded with the
// matching moments in a REFERENCE clip (e.g. the photographer's own graded
// delivery of the same footage). The pairing is stored on the asset doc as ids
// + times only ({ refAssetId, pairs: [{ t, refT }] }, source seconds); the
// frames themselves stay in S3 and are recaptured on demand. The Grok stage
// assist then samples the clip AT the paired keyframes and sends each one's
// reference frame alongside, so the model reproduces the intended look instead
// of freelancing taste.
//
//   tripId / assetId  the clip asset being graded (where the pairing persists)
//   clipUrl           playable URL of the clip being graded (edit proxy)
//   seg               the active segment: { inSec, outEff, srcLen } in source s
//   savedRef          asset.gradeRef (live via the parent's asset subscription)
//   disabled          block interactions (no preview yet)
const THUMB_W = 320;
const KEYFRAME_FRACS = [0.15, 0.5, 0.85];

// Stable, non-expiring source for a reference asset: CDN edit/delivery proxies
// first; fall back marker '' means "presign refS3Key at capture time".
export const stableAssetUrl = (a) => httpsify(a?.editMp4Url || a?.editProxyUrl || a?.proxyHlsUrl || a?.url || '');

// Resolve a saved gradeRef to a playable URL (stable CDN url, else presigned).
export async function resolveGradeRefUrl(gradeRef) {
  if (!gradeRef) return '';
  if (gradeRef.refUrl) return httpsify(gradeRef.refUrl);
  if (gradeRef.refS3Key) {
    try { return await getAssetPlaybackUrl(gradeRef.refS3Key); } catch { return ''; }
  }
  return '';
}

export default function ReferenceMatch({ tripId, assetId, clipUrl, seg, savedRef, disabled = false }) {
  const { docs: assets } = useTripAssets(tripId || null);
  const [open, setOpen] = useState(false);
  const [refAsset, setRefAsset] = useState(null); // the picked reference asset doc
  const [rows, setRows] = useState([]); // [{ t, thumb, refT, refThumb }]
  const [sel, setSel] = useState(0); // selected row index (receives "Set match")
  const [refDur, setRefDur] = useState(0);
  const [refT, setRefT] = useState(0); // reference player position
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const hydratedFor = useRef(''); // assetId the rows were built for

  const candidates = useMemo(
    () => (assets || []).filter((a) => a.kind !== 'photo' && a.id !== assetId),
    [assets, assetId],
  );

  const inSec = seg?.inSec || 0;
  const srcLen = Math.max(0, (seg?.outEff || 0) - inSec) || seg?.srcLen || 0;

  // (Re)build the keyframe rows when the studio moves to another clip: from the
  // saved pairing when one exists, else defaults spread across the trim window.
  useEffect(() => {
    if (hydratedFor.current === assetId) return;
    hydratedFor.current = assetId;
    setErr('');
    setDirty(false);
    setSel(0);
    if (savedRef && Array.isArray(savedRef.pairs) && savedRef.pairs.length) {
      setRows(savedRef.pairs.map((p) => ({ t: p.t, thumb: '', refT: Number.isFinite(p.refT) ? p.refT : null, refThumb: '' })));
    } else {
      setRows(KEYFRAME_FRACS.map((f) => ({ t: inSec + srcLen * f, thumb: '', refT: null, refThumb: '' })));
    }
    // The picked reference follows the saved doc; a fresh clip starts unpicked.
    setRefAsset(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Adopt the saved reference asset once the trip assets are in.
  useEffect(() => {
    if (refAsset || !savedRef?.refAssetId || !assets) return;
    const a = assets.find((x) => x.id === savedRef.refAssetId);
    if (a) setRefAsset(a);
  }, [assets, savedRef, refAsset]);

  // Lazy keyframe thumbnails of the RAW clip (only while the panel is open).
  useEffect(() => {
    if (!open || !clipUrl) return undefined;
    let alive = true;
    (async () => {
      for (let i = 0; i < rows.length; i += 1) {
        if (!alive || rows[i].thumb) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const f = await captureFrameAt({ url: clipUrl, atSec: rows[i].t, maxW: THUMB_W });
          if (!alive) return;
          setRows((rs) => rs.map((r, j) => (j === i ? { ...r, thumb: f.rawUrl } : r)));
        } catch { /* leave the row thumbless — pairing still works */ }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clipUrl, rows.length]);

  // Attach the reference video (hls.js for .m3u8, native otherwise).
  useEffect(() => {
    const v = videoRef.current;
    if (!open || !v || !refAsset) return undefined;
    let alive = true;
    setRefDur(0);
    (async () => {
      let url = stableAssetUrl(refAsset);
      if (!url && refAsset.s3Key) {
        try { url = await getAssetPlaybackUrl(refAsset.s3Key); } catch { url = ''; }
      }
      if (!alive || !url) { if (alive) setErr('No playable source for that reference clip.'); return; }
      const isHls = /\.m3u8(\?|$)/.test(url);
      if (isHls && !v.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hls.loadSource(url);
        hls.attachMedia(v);
        hlsRef.current = hls;
      } else {
        v.src = url;
        v.load();
      }
    })();
    const onMeta = () => setRefDur(Number.isFinite(v.duration) ? v.duration : 0);
    v.addEventListener('loadedmetadata', onMeta);
    return () => {
      alive = false;
      v.removeEventListener('loadedmetadata', onMeta);
      try { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } } catch { /* noop */ }
      try { v.removeAttribute('src'); v.load(); } catch { /* noop */ }
    };
  }, [open, refAsset]);

  if (!tripId || !assetId || !seg) return null;

  const scrubRef = (sec) => {
    const v = videoRef.current;
    const t = Math.max(0, Math.min(sec, Math.max(0, refDur - 0.05)));
    setRefT(t);
    if (v) { try { v.currentTime = t; } catch { /* noop */ } }
  };

  // One reference-video frame, from the asset's recorded rate (edit proxies
  // keep the source fps). 25 is only the emergency fallback.
  const refFrameSec = 1 / (refAsset?.proxyFps || refAsset?.srcFps || 25);
  const stepRef = (frames) => scrubRef(refT + frames * refFrameSec);

  const setMatch = () => {
    const v = videoRef.current;
    if (!v || sel < 0 || sel >= rows.length) return;
    const thumb = scaleToDataUrl(v, THUMB_W, 0.8) || '';
    setRows((rs) => rs.map((r, j) => (j === sel ? { ...r, refT: v.currentTime, refThumb: thumb } : r)));
    setDirty(true);
    // Convenience: advance to the next unmatched keyframe.
    setSel((s) => {
      for (let j = 1; j <= rows.length; j += 1) {
        const k = (s + j) % rows.length;
        if (rows[k].refT == null && k !== s) return k;
      }
      return s;
    });
  };

  const save = async () => {
    if (!refAsset || busy) return;
    const pairs = rows
      .filter((r) => Number.isFinite(r.refT))
      .map((r) => ({ t: Math.round(r.t * 100) / 100, refT: Math.round(r.refT * 100) / 100 }));
    if (!pairs.length) { setErr('Match at least one keyframe first.'); return; }
    setBusy(true);
    setErr('');
    try {
      await updateAssetGradeRef(tripId, assetId, {
        refTripId: tripId,
        refAssetId: refAsset.id,
        refName: refAsset.name || 'Reference clip',
        refUrl: stableAssetUrl(refAsset),
        refS3Key: refAsset.s3Key || '',
        pairs,
        updatedAt: Date.now(),
      });
      setDirty(false);
    } catch {
      setErr('Couldn’t save the reference pairing.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await updateAssetGradeRef(tripId, assetId, null);
      setRefAsset(null);
      setRows(KEYFRAME_FRACS.map((f) => ({ t: inSec + srcLen * f, thumb: '', refT: null, refThumb: '' })));
      setDirty(false);
    } catch {
      setErr('Couldn’t clear the reference pairing.');
    } finally {
      setBusy(false);
    }
  };

  const matched = rows.filter((r) => Number.isFinite(r.refT)).length;
  const savedPairs = Array.isArray(savedRef?.pairs) ? savedRef.pairs.length : 0;

  return (
    <div className="cs-refmatch">
      <div className="cs-refmatch-row">
        <button
          type="button"
          className={`cs-btn cs-btn-ghost cs-btn-sm${savedPairs ? ' cs-refmatch-on' : ''}`}
          disabled={disabled}
          title="Pair keyframes of this raw clip with the matching moments in a graded reference clip (e.g. the photographer's delivery) — Grok then grades toward that look instead of generic taste"
          onClick={() => setOpen((o) => !o)}
        >
          🎯 Reference match{savedPairs ? ` · ${savedRef.refName || 'ref'} · ${savedPairs} pair${savedPairs > 1 ? 's' : ''}` : ''}
        </button>
        {savedPairs > 0 && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy || disabled} onClick={clear} title="Remove the saved reference pairing">
            ✕ Clear
          </button>
        )}
      </div>
      {open && (
        <div className="cs-refmatch-body">
          {!refAsset && (
            <div className="cs-refmatch-pick">
              <div className="cs-refmatch-hint">Pick the reference clip (the graded delivery this footage appears in):</div>
              {candidates.length ? candidates.map((a) => (
                <button key={a.id} type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setRefAsset(a); setErr(''); }}>
                  {a.name || a.id}{a.durationSec ? ` · ${fmtTime(a.durationSec)}` : ''}
                </button>
              )) : <div className="cs-refmatch-hint">No other video assets in this trip.</div>}
            </div>
          )}
          {refAsset && (
            <>
              <div className="cs-refmatch-hint">
                <b>{refAsset.name || 'Reference'}</b> — select a keyframe, scrub the reference to the matching moment (same wave/light, any graded clip works), then <b>Set match</b>.
              </div>
              <div className="cs-refmatch-grid">
                <div className="cs-refmatch-keys">
                  {rows.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`cs-refmatch-key${i === sel ? ' on' : ''}`}
                      onClick={() => { setSel(i); if (Number.isFinite(r.refT)) scrubRef(r.refT); }}
                      title={`Keyframe @ ${fmtTime(Math.max(0, r.t - inSec))} into the clip`}
                    >
                      {r.thumb ? <img src={r.thumb} alt="" /> : <span className="cs-refmatch-ph">…</span>}
                      {r.refThumb ? <img src={r.refThumb} alt="" /> : <span className="cs-refmatch-ph">{Number.isFinite(r.refT) ? fmtTime(r.refT) : 'unmatched'}</span>}
                      <span className="cs-refmatch-keytag">
                        {fmtTime(Math.max(0, r.t - inSec))}{Number.isFinite(r.refT) ? ` ↔ ${fmtTime(r.refT)}` : ' ↔ —'}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="cs-refmatch-player">
                  {/* muted+playsInline so scrubbing never audibly plays */}
                  <video ref={videoRef} muted playsInline crossOrigin="anonymous" preload="auto" />
                  <div className="cs-refmatch-scrub">
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0.1, refDur)}
                      step={0.04}
                      value={refT}
                      onChange={(e) => scrubRef(Number(e.target.value))}
                    />
                    <span>{fmtTime(refT)} / {fmtTime(refDur)}</span>
                  </div>
                  {/* Frame-accurate nudges for landing on the exact matching frame:
                      scrub gets close, then step ±10 / ±1 frames. */}
                  <div className="cs-refmatch-step">
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!refDur} onClick={() => stepRef(-10)} title="Back 10 frames">
                      ‹‹ 10f
                    </button>
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!refDur} onClick={() => stepRef(-1)} title="Back 1 frame">
                      ‹ 1f
                    </button>
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!refDur} onClick={() => stepRef(1)} title="Forward 1 frame">
                      1f ›
                    </button>
                    <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!refDur} onClick={() => stepRef(10)} title="Forward 10 frames">
                      10f ››
                    </button>
                  </div>
                  <div className="cs-refmatch-actions">
                    <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!refDur} onClick={setMatch}>
                      Set match for keyframe {sel + 1}
                    </button>
                    <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !matched || !dirty} onClick={save}>
                      {busy ? 'Saving…' : dirty ? `Save ${matched} pair${matched > 1 ? 's' : ''}` : '✓ Saved'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          {err && <div className="cs-grok-err">{err}</div>}
        </div>
      )}
    </div>
  );
}
